import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock3, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const STATUS_LABELS = {
  approved: {
    title: "Pago confirmado",
    message: "Tu abono fue procesado correctamente y tu reserva ya está asegurada.",
    icon: <CheckCircle2 className="w-10 h-10 text-[#1F7A3F]" />,
  },
  success: {
    title: "Pago confirmado",
    message: "Tu pago fue aceptado por Mercado Pago. Estamos verificando el estado final de la reserva.",
    icon: <CheckCircle2 className="w-10 h-10 text-[#1F7A3F]" />,
  },
  pending: {
    title: "Pago pendiente",
    message: "Estamos confirmando tu pago. Si el proceso demora, vuelve a intentarlo en unos minutos.",
    icon: <Clock3 className="w-10 h-10 text-[#B5704A]" />,
  },
  in_process: {
    title: "Pago en proceso",
    message: "Mercado Pago está procesando tu transacción. Revisa el estado en unos instantes.",
    icon: <Clock3 className="w-10 h-10 text-[#B5704A]" />,
  },
  rejected: {
    title: "Pago rechazado",
    message: "El pago no se completó. Puedes intentarlo nuevamente desde la página de reserva.",
    icon: <XCircle className="w-10 h-10 text-[#A82E2E]" />,
  },
  failure: {
    title: "Pago rechazado",
    message: "Mercado Pago rechazó la transacción. Puedes intentar nuevamente desde la página de reserva.",
    icon: <XCircle className="w-10 h-10 text-[#A82E2E]" />,
  },
  cancelled: {
    title: "Pago cancelado",
    message: "No se registró un pago para esta reserva. Vuelve a reservar cuando quieras.",
    icon: <XCircle className="w-10 h-10 text-[#A82E2E]" />,
  },
};

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [appointmentStatus, setAppointmentStatus] = useState(null);
  const [amountClp, setAmountClp] = useState(null);
  const [service, setService] = useState(null);
  const [start, setStart] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const apptId = searchParams.get("appt");
  const returnStatus = searchParams.get("status");

  useEffect(() => {
    if (!apptId) {
      setError("No se encontró el identificador de la reserva.");
      setLoading(false);
      return;
    }

    let interval;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/payments/mp/status", {
          params: { appointment_id: apptId },
        });
        setPaymentStatus(data.payment_status || null);
        setAppointmentStatus(data.status || null);
        setAmountClp(data.amount_clp || null);
        setService(data.service || null);
        setStart(data.start || null);
        setError(null);
      } catch (e) {
        setError(e.response?.data?.detail || "No pudimos obtener el estado de pago.");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();

    if (returnStatus !== "failure") {
      interval = setInterval(() => {
        setPollCount((prev) => prev + 1);
      }, 5000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [apptId, returnStatus, pollCount]);

  const currentStatus = paymentStatus || returnStatus || "pending";
  const statusConfig = STATUS_LABELS[currentStatus] || {
    title: "Estado desconocido",
    message: "No tenemos información del pago en este momento.",
    icon: <Clock3 className="w-10 h-10 text-[#B5704A]" />,
  };

  return (
    <div className="min-h-screen bg-[#F4EFE4] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="text-sm text-[#6F5A4C] hover:text-[#B5704A]">
            <ArrowLeft className="inline-block mr-2" /> Volver al inicio
          </Link>
          <span className="text-xs uppercase tracking-[0.2em] text-[#B5704A]">Confirmación de pago</span>
        </div>

        <div className="surface-card p-8 rounded-3xl shadow-sm bg-white">
          {loading ? (
            <div className="text-center">
              <div className="h-20 w-20 mx-auto rounded-full bg-[#F4EFE4] flex items-center justify-center mb-6">
                <Clock3 className="w-10 h-10 text-[#B5704A] animate-spin" />
              </div>
              <div className="text-lg font-semibold text-[#3A2418]">Consultando el estado de tu pago…</div>
              <p className="mt-2 text-sm text-[#6F5A4C]">Esto puede tardar unos segundos mientras Mercado Pago confirma la transacción.</p>
            </div>
          ) : error ? (
            <div className="text-center">
              <XCircle className="mx-auto w-16 h-16 text-[#A82E2E] mb-4" />
              <div className="text-lg font-semibold text-[#3A2418]">No pudimos cargar el estado</div>
              <p className="mt-2 text-sm text-[#6F5A4C]">{error}</p>
            </div>
          ) : (
            <div className="text-center">
              <div className="mx-auto mb-6">{statusConfig.icon}</div>
              <h1 className="text-3xl font-display font-[500] text-[#3A2418] mb-3">{statusConfig.title}</h1>
              <p className="text-sm text-[#6F5A4C] mb-6">{statusConfig.message}</p>

              {service && (
                <div className="rounded-3xl border border-[#E8DFD2] bg-[#FAF7F1] p-4 text-left text-sm text-[#3A2418]">
                  <div className="font-medium mb-2">Detalle de la reserva</div>
                  <div>{service}</div>
                  {start && <div className="mt-1">{new Date(start).toLocaleString("es-CL", { dateStyle: "long", timeStyle: "short", timeZone: "America/Santiago" })}</div>}
                  {amountClp && <div className="mt-1">Anticipo: ${amountClp.toLocaleString("es-CL")}</div>}
                  <div className="mt-1">Estado de la reserva: <strong>{appointmentStatus || "pendiente"}</strong></div>
                </div>
              )}

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button asChild variant="outline">
                  <Link to="/reservar">Reservar otra fecha</Link>
                </Button>
                <Button onClick={() => window.location.reload()} className="bg-[#B5704A] text-white hover:bg-[#9C5E3D]">
                  Refrescar estado
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
